import { useState, useEffect, useCallback } from 'react';
import { getPrequelAPIKey } from '../utils/authToken';
import { PREQUEL_HOST } from '../constants/prequel';
import { Destination } from '../contexts/@prequel/type';

export interface UseDestinationResult {
  destination: Destination | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch a single destination by ID using the specific endpoint
 * This is much more efficient than fetching all destinations and then finding by ID
 *
 * @param destinationId - The ID of the destination to fetch
 * @returns Object containing destination data, loading state, error state, and refetch function
 */
export const useDestination = (destinationId: string): UseDestinationResult => {
  const [destination, setDestination] = useState<Destination | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDestination = useCallback(async () => {
    if (!destinationId) {
      setError('Destination ID is required');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get the scoped auth token
      const apiKey = getPrequelAPIKey();
      if (!apiKey) {
        throw new Error('Failed to get authentication token');
      }

      const response = await fetch(`${PREQUEL_HOST}/destinations/${destinationId}`, {
        method: 'GET',
        headers: {
          'X-API-KEY': apiKey,
          'Content-Type': 'application/json',
          'X-Prequel-Api-Version': '2023-12-01',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Destination with ID '${destinationId}' not found`);
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.status === 'success' && result.data?.destination) {
        setDestination(result.data.destination);
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (err) {
      setError(
        `Failed to fetch destination: ${err instanceof Error ? err.message : 'Unknown error'}`,
      );
      setDestination(null);
    } finally {
      setIsLoading(false);
    }
  }, [destinationId]);

  useEffect(() => {
    fetchDestination();
  }, [fetchDestination]);

  return {
    destination,
    isLoading,
    error,
    refetch: fetchDestination,
  };
};
